import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import ModalPortal from './ModalPortal';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: string;
  hideCloseButton?: boolean;
  preventOutsideClick?: boolean;
  zIndex?: number;
}

// Memoize the Modal component to prevent unnecessary re-renders
export const Modal: React.FC<ModalProps> = React.memo(({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '500px',
  hideCloseButton = false,
  preventOutsideClick = false,
  zIndex = 9999
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Memoize the close handler to prevent re-renders
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Enhanced scroll prevention for mobile
  useEffect(() => {
    if (!isOpen) return;
    
    // Store original body styles
    const originalStyle = window.getComputedStyle(document.body);
    const originalOverflow = originalStyle.overflow;
    const originalPosition = originalStyle.position;
    const originalTop = originalStyle.top;
    const originalLeft = originalStyle.left;
    const originalWidth = originalStyle.width;
    const originalHeight = originalStyle.height;
    
    // Prevent scrolling on body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Prevent scrolling on html as well (for iOS)
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.top = '0';
    document.documentElement.style.left = '0';
    document.documentElement.style.width = '100%';
    document.documentElement.style.height = '100%';

    return () => {
      // Restore original styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
      
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.top = '';
      document.documentElement.style.left = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
    };
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen || preventOutsideClick) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose, preventOutsideClick]);

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div 
        className="fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
        style={{ zIndex }}
      >
        <div 
          ref={modalRef} 
          style={{ maxWidth }}
          className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-xl shadow-lg w-full max-h-[90vh] overflow-hidden flex flex-col animate-slide-up"
        >
          <div className="flex justify-between items-center px-6 py-4 border-b border-border-light dark:border-border-dark">
            {title && (
              <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                {title}
              </h2>
            )}
            {!hideCloseButton && (
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-background-light dark:hover:bg-background-dark transition-colors"
              >
                <X size={20} className="text-text-secondary-light dark:text-text-secondary-dark" />
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
            {children}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
});

Modal.displayName = 'Modal';

export default Modal; 